import React from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock,
  CreditCard,
  FileText,
  Loader2,
  LogOut,
  Mail,
  Sparkles,
  User as UserIcon,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { VerifyEmailBanner } from "@/components/VerifyEmailBanner";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_NAMES = { cv_basic: "BASIC", cv_pro: "PRO", cv_elite: "ELITE" };

export default function Account() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = React.useState(null);
  const [jobs, setJobs] = React.useState(null);

  usePageMeta({
    title: "My account | CV AI PRO",
    description: "Your CV AI PRO account.",
    path: "/account",
  });

  React.useEffect(() => {
    if (user === false) navigate("/login?next=/account", { replace: true });
  }, [user, navigate]);

  React.useEffect(() => {
    if (!user || user === false) return;
    axios
      .get(`${API}/payments/orders`, { withCredentials: true })
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders({ orders: [], available_count: 0 }));
    axios
      .get(`${API}/cv/jobs`, { withCredentials: true })
      .then(({ data }) => setJobs(data.jobs || []))
      .catch(() => setJobs([]));
  }, [user]);

  if (!user || user === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-sm text-zinc-500">Loading…</div>
      </div>
    );
  }

  const handleLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/", { replace: true });
  };

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="account-page">
      <div aria-hidden className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-indigo-600/15 blur-[140px]" />
      <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg opacity-50" />

      <header className="relative z-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="account-logo">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-blue-500/80 to-purple-600/80 ring-1 ring-white/10">
              <Sparkles className="h-3.5 w-3.5 text-white" />
            </span>
            <span className="text-sm font-semibold tracking-tight text-white">
              CV AI <span className="text-zinc-400 font-medium">PRO</span>
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white transition-colors"
            data-testid="account-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </Link>
        </div>
      </header>

      <main className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Account</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-white" data-testid="account-headline">
            Welcome back, {user.name || user.email.split("@")[0]}.
          </h1>
          <p className="mt-4 text-zinc-400">
            Manage your CV AI PRO orders and delivered resumes.
          </p>
          <div className="mt-6">
            <VerifyEmailBanner />
          </div>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mt-12 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8"
        >
          <div className="flex items-center gap-4">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500/40 to-purple-500/40 ring-1 ring-white/10 text-white text-sm font-semibold">
              {(user.name || user.email)[0]?.toUpperCase()}
            </span>
            <div>
              <p className="text-white text-lg font-medium leading-tight" data-testid="account-name">
                {user.name || "—"}
              </p>
              <p className="text-xs text-zinc-500 flex items-center gap-1.5 mt-1" data-testid="account-email">
                <Mail className="h-3 w-3" />
                {user.email}
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/upload"
              className="group rounded-2xl border border-white/10 bg-zinc-950/60 p-5 hover:border-white/20 hover:bg-zinc-950/80 transition-colors"
              data-testid="account-link-upload"
            >
              <UserIcon className="h-4 w-4 text-zinc-400" />
              <p className="mt-3 text-sm text-white font-medium">Create a new AI resume</p>
              <p className="mt-1 text-xs text-zinc-500">
                {orders
                  ? orders.available_count > 0
                    ? `${orders.available_count} AI credit${orders.available_count > 1 ? "s" : ""} available.`
                    : "Purchase a plan to unlock the AI studio."
                  : "Upload your CV for optimization."}
              </p>
              <ArrowRight className="mt-4 h-4 w-4 text-zinc-500 group-hover:text-white transition-colors" />
            </Link>
            <a
              href="mailto:getcvaipro@gmail.com?subject=Order%20support"
              className="group rounded-2xl border border-white/10 bg-zinc-950/60 p-5 hover:border-white/20 hover:bg-zinc-950/80 transition-colors"
              data-testid="account-link-support"
            >
              <Mail className="h-4 w-4 text-zinc-400" />
              <p className="mt-3 text-sm text-white font-medium">Contact support</p>
              <p className="mt-1 text-xs text-zinc-500">Get help with an order.</p>
              <ArrowRight className="mt-4 h-4 w-4 text-zinc-500 group-hover:text-white transition-colors" />
            </a>
          </div>

          <button
            onClick={handleLogout}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm text-white hover:bg-white/10 transition-colors"
            data-testid="account-logout"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </motion.section>

        {/* My AI resumes */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.18, duration: 0.6 }}
          className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8"
          data-testid="account-resumes-section"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">My AI resumes</p>
          {jobs === null ? (
            <div className="mt-6 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-zinc-600" />
            </div>
          ) : jobs.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500" data-testid="account-no-resumes">
              No resumes yet — your AI-generated CVs will appear here.
            </p>
          ) : (
            <div className="mt-5 space-y-3">
              {jobs.map((job) => (
                <Link
                  key={job.id}
                  to={`/upload?job=${job.id}`}
                  className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-4 hover:border-white/20 transition-colors"
                  data-testid={`account-resume-${job.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-black">
                      <FileText className="h-4 w-4 text-zinc-400" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{job.desired_role || job.full_name || "AI Resume"}</p>
                      <p className="text-[11px] text-zinc-500">
                        {job.created_at ? new Date(job.created_at).toLocaleDateString() : ""} ·{" "}
                        {PLAN_NAMES[job.plan] || "Plan"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {job.status === "completed" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-1 text-[11px] text-emerald-300">
                        <CheckCircle2 className="h-3 w-3" />
                        {job.analysis?.score_after ? `Score ${job.analysis.score_after}` : "Ready"}
                      </span>
                    ) : job.status === "processing" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/25 bg-blue-500/10 px-2.5 py-1 text-[11px] text-blue-300">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Processing
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/25 bg-red-500/10 px-2.5 py-1 text-[11px] text-red-300">
                        Failed
                      </span>
                    )}
                    <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-white transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.section>

        {/* Orders */}
        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.26, duration: 0.6 }}
          className="mt-6 rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-950 to-black p-8"
          data-testid="account-orders-section"
        >
          <p className="text-xs uppercase tracking-[0.18em] text-zinc-500">Orders</p>
          {orders === null ? (
            <div className="mt-6 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-zinc-600" />
            </div>
          ) : orders.orders.length === 0 ? (
            <p className="mt-4 text-sm text-zinc-500" data-testid="account-no-orders">
              No orders yet.
            </p>
          ) : (
            <div className="mt-5 space-y-3">
              {orders.orders.map((o) => (
                <div
                  key={o.session_id}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-zinc-950/60 p-4"
                  data-testid={`account-order-${o.session_id.slice(-8)}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-black">
                      <CreditCard className="h-4 w-4 text-zinc-400" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-white">
                        {PLAN_NAMES[o.lookup_key] || o.lookup_key} — ${(o.amount / 100).toFixed(2)}
                      </p>
                      <p className="text-[11px] text-zinc-500">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}
                        {o.used ? " · Credit used" : o.payment_status === "paid" ? " · Credit available" : ""}
                      </p>
                    </div>
                  </div>
                  {o.payment_status === "paid" ? (
                    o.used && o.job_id ? (
                      <Link
                        to={`/upload?job=${o.job_id}`}
                        className="text-[11px] text-zinc-300 hover:text-white underline underline-offset-2 shrink-0"
                        data-testid={`order-view-resume-${o.session_id.slice(-8)}`}
                      >
                        View resume
                      </Link>
                    ) : !o.used ? (
                      <Link
                        to="/upload"
                        className="text-[11px] text-emerald-300 hover:text-emerald-200 underline underline-offset-2 shrink-0"
                        data-testid={`order-use-credit-${o.session_id.slice(-8)}`}
                      >
                        Use credit
                      </Link>
                    ) : null
                  ) : (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-300 shrink-0">
                      <Clock className="h-3 w-3" />
                      {o.payment_status}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </motion.section>
      </main>
    </div>
  );
}
